import { useCallback, useState } from 'react';
import styled, { css } from 'foundations-theming-specialty/utils';
import TrackedLink from '../TrackedLink';
import LinkBadge from '../LinkBadge';
import { MenuItemLevel } from '../../constants';
import useVerticalNavFocus from './hooks/useVerticalNavFocus';
import convertToAbsolutePath from '../../utils/convertToAbsolutePath';
import { ButtonWithDemoExitConfirmation } from '../DemoExitConfirmation/ButtonWithDemoExitConfirmation';
import { useNavBookmarks } from '../../hooks/useNavBookmarks/useNavBookmarks';
import { Bookmark } from '../Bookmarks/Bookmark';
import withTransientStyleProps from '../../utils/withTransientStyleProps';
import { navConfigCache } from '../../config/cache';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledLink = styled(withTransientStyleProps(TrackedLink))(({
  theme,
  $shouldHighlight
}) => {
  return css(["display:flex;justify-content:space-between;padding:8px 16px;color:", ";font-size:14px;font-weight:300;line-height:24px;background:", ";width:100%;text-align:left;margin:0 0 4px 0;border:0;border-radius:6px;align-items:center;position:relative;&:hover{text-decoration:none !important;background:", " !important;color:", " !important;}&::before,&::after{content:'';height:100%;position:absolute;width:18px;}&::before{left:-18px;}&::after{right:-18px;}ul{list-style-type:none;padding:0;margin:0 0 0 12px;}"], theme.color['global-shell-text-nav-item-hover'], $shouldHighlight ? `${theme.color['global-shell-fill-nav-item-selected']} !important` : 'transparent', theme.color['global-shell-fill-nav-item-selected'], theme.color['global-shell-text-nav-item-hover']);
});
export const StyledIcons = styled.div.withConfig({
  displayName: "VerticalNavSecondaryMenuItem__StyledIcons",
  componentId: "sc-198obj7-0"
})(["display:flex;align-items:center;"]);
const VerticalNavSecondaryMenuItem = ({
  id,
  path,
  label,
  onKeyDown,
  shouldHighlight,
  linkBadgeData,
  menuItemLevel = MenuItemLevel.SECONDARY,
  primaryCategory,
  isBookmarked,
  canBeBookmarked = true,
  isBookmarksMenu,
  children
}) => {
  const [isHoveringOverNavItem, setIsHoveringOverNavItem] = useState(false);
  const [isHoveringOverBookmarks, setIsHoveringOverBookmarks] = useState(false);
  const {
    ref
  } = useVerticalNavFocus({
    id
  });
  const href = convertToAbsolutePath(path);
  const linkBadgeTooltipId = linkBadgeData ? `${id}-tooltip` : undefined;
  const {
    addNavItemToBookmarks,
    removeNavItemFromBookmarks
  } = useNavBookmarks();
  const onBookmarkClicked = useCallback(bookmarkState => {
    if (!canBeBookmarked) {
      return;
    }
    if (bookmarkState) {
      addNavItemToBookmarks({
        id,
        path: path || ''
      });
    } else {
      removeNavItemFromBookmarks(id);
    }
    // Flush the nav config cache to ensure it's updated on next page load
    navConfigCache.delete();
  }, [addNavItemToBookmarks, id, canBeBookmarked, path, removeNavItemFromBookmarks]);
  const onNavItemHover = useCallback(() => {
    setIsHoveringOverNavItem(true);
  }, [setIsHoveringOverNavItem]);
  const onNavItemBlur = useCallback(() => {
    setIsHoveringOverNavItem(false);
  }, [setIsHoveringOverNavItem]);
  return /*#__PURE__*/_jsxs("li", {
    role: "none",
    children: [/*#__PURE__*/_jsxs(ButtonWithDemoExitConfirmation, {
      component: StyledLink,
      $shouldHighlight: shouldHighlight,
      id: id,
      "data-test-id": id,
      href: href,
      onFocus: onNavItemHover,
      onBlur: onNavItemBlur,
      onMouseEnter: onNavItemHover,
      onMouseLeave: onNavItemBlur,
      onKeyDown: onKeyDown,
      innerRef: ref,
      role: "menuitem",
      "data-menu-item-level": menuItemLevel,
      "data-menu-item-group": primaryCategory,
      "aria-describedby": linkBadgeTooltipId,
      "aria-label": label,
      children: [/*#__PURE__*/_jsx("span", {
        className: "label",
        children: label
      }), /*#__PURE__*/_jsxs(StyledIcons, {
        children: [canBeBookmarked && /*#__PURE__*/_jsx(Bookmark, {
          isBookmarked: !!isBookmarked,
          onBookmarkClicked: onBookmarkClicked,
          rightSideBadgeType: linkBadgeData ? linkBadgeData.type : null,
          bookmarkId: id,
          bookmarkPrimaryNavCategory: primaryCategory,
          bookmarkLabel: label,
          isHoveringOverBookmarks: isHoveringOverBookmarks,
          setIsHoveringOverBookmarks: setIsHoveringOverBookmarks,
          displayLinkBadge: !!linkBadgeData,
          isBookmarksMenu: isBookmarksMenu,
          isHoveringOverNavItem: isHoveringOverNavItem
        }), linkBadgeData ? /*#__PURE__*/_jsx(Flex, {
          marginInlineStart: "100",
          blockSize: "100%",
          children: /*#__PURE__*/_jsx(LinkBadge, {
            type: linkBadgeData.type,
            text: linkBadgeData.text,
            tooltipText: linkBadgeData.tooltipText,
            tooltipPlacement: "right",
            tooltipPadding: 40,
            isHovering: isHoveringOverNavItem && !isHoveringOverBookmarks,
            tooltipId: linkBadgeTooltipId
          })
        }) : null]
      })]
    }), children]
  });
};
export default VerticalNavSecondaryMenuItem;